import React from "react";
import { IonIcon } from "@ionic/react";
import { videocam } from "ionicons/icons";
import "lightgallery/css/lightgallery.css";
import "lightgallery/css/lg-zoom.css";
import "lightgallery/css/lg-thumbnail.css";
import "lightgallery/css/lg-video.css";
import "lightgallery/css/lg-pager.css";
import "lightgallery/css/lg-rotate.css";
import LightGallery from "lightgallery/react";
import lgVideo from "lightgallery/plugins/video";
import lgThumbnail from "lightgallery/plugins/thumbnail";
import lgZoom from "lightgallery/plugins/zoom";
import lgRotate from "lightgallery/plugins/rotate";
import lgPager from "lightgallery/plugins/pager";

export interface MediaItem {
  file: string;
  isImage: boolean;
  thumbnail?: string;
  name?: string;
}

export interface LightGalleryComponentProps {
  items: MediaItem[];
  licenseKey?: string;
  speed?: number;
  controls?: boolean;
  zoom?: boolean;
  mobileSettings?: {
    controls?: boolean;
    showCloseIcon?: boolean;
    showZoomInOutIcons?: boolean;
    download?: boolean;
    zoom?: boolean;
  };
  itemClassName?: string;
  imageClassName?: string;
  videoContainerClassName?: string;
  onError?: (index: number) => void;
  enablePlugins?: {
    video?: boolean;
    zoom?: boolean;
    thumbnail?: boolean;
    rotate?: boolean;
    pager?: boolean;
  };
}

const LightGalleryComponent: React.FC<LightGalleryComponentProps> = ({
  items,
  licenseKey = "Thki",
  speed = 500,
  controls = true,
  zoom = true,
  mobileSettings = {
    controls: true,
    showCloseIcon: true,
    showZoomInOutIcons: true,
    download: true,
    zoom: true,
  },
  itemClassName = "gallery-item ml-3 border-2 border-solid border-paragraphColor rounded-lg",
  imageClassName = "w-28 h-28 object-cover",
  videoContainerClassName = "relative w-28 h-28",
  onError,
  enablePlugins = {
    video: true,
    zoom: true,
    thumbnail: true,
    rotate: true,
    pager: true,
  },
}) => {
  // Build plugins array based on enablePlugins configuration
  const plugins = [];
  if (enablePlugins.video) plugins.push(lgVideo);
  if (enablePlugins.zoom) plugins.push(lgZoom);
  if (enablePlugins.thumbnail) plugins.push(lgThumbnail);
  if (enablePlugins.rotate) plugins.push(lgRotate);
  if (enablePlugins.pager) plugins.push(lgPager);

  const handleError = (index: number) => {
    if (onError) {
      onError(index);
    }
  };

  const getAltText = (item: MediaItem, index: number) => {
    if (item.name) {
      return item.name.split(".")[0].split("-")[1] || `Media item ${index + 1}`;
    }
    return `Media item ${index + 1}`;
  };

  return (
    <LightGallery
      licenseKey={licenseKey}
      speed={speed}
      plugins={plugins}
      controls={controls}
      zoom={zoom}
      mobileSettings={mobileSettings}
    >
      {items.map((item: MediaItem, index: number) => {
        // Add CSS classes to the gallery container
        setTimeout(() => {
          document
            .querySelector(".lg-react-element")
            ?.classList.add("flex", "space-x-3");
        }, 0);

        return (
          <div
            key={index}
            className={itemClassName}
            data-src={item.isImage ? item.file : undefined}
            data-video={
              item.isImage
                ? undefined
                : `{"source": [{"src":"${item.file}", "type":"video/mp4"}], "attributes": {"preload": false, "controls": true}}`
            }
          >
            {item.isImage ? (
              <img
                src={item.file}
                alt={getAltText(item, index)}
                className={imageClassName}
                onError={() => handleError(index)}
              />
            ) : (
              <div
                className={videoContainerClassName}
                data-video={`{"source": [{"src":"${item.file}", "type":"video/mp4"}], "attributes": {"preload": false, "controls": true}}`}
              >
                <img
                  src={item.thumbnail || item.file}
                  className={imageClassName}
                  alt={getAltText(item, index)}
                  onError={() => handleError(index)}
                />
                <IonIcon
                  icon={videocam}
                  className="absolute z-10 w-12 h-12 inset-0 m-auto text-primaryColor100"
                />
              </div>
            )}
          </div>
        );
      })}
    </LightGallery>
  );
};

export default LightGalleryComponent;
